<!-- Schedule.vue - List View with API Integration -->
<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back to Home -->
    <div class="back-home text-left mb-3">
      <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/kids-device', query: { profileId: this.profileId, serialNumber: serialNumber } })">
        <i class="fas fa-arrow-left"></i> <b>Back to Home</b>
      </a>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading schedules...</p>
    </div>

    <!-- Content (shown when not loading) -->
    <template v-else>
      <div class="back-home text-right mb-2 pr-1">
        <button
          class="btn btn-primary px-4"
          :disabled="isDisabled"
          @click="navigateToCreate()"
        >
          Create Schedule
        </button>
      </div>
      <!-- Page Header -->
      <!--<div class="schedule-header text-left">
        <div class="header-content">
          <div class="header-text">
            <h4 class="mb-2"><i class="fas fa-calendar-alt mr-2"></i>Device Schedules</h4>
            <p class="mb-0">Manage when and how your child can use their device</p>
          </div>
          <button class="btn btn-add-schedule" @click="navigateToCreate()">
            <i class="fas fa-plus-circle mr-2"></i>Create Schedule
          </button>
        </div>
      </div>-->

      <!-- Stats Summary -->
      <!--<div class="stats-container" v-if="schedules.length > 0">
        <div class="stat-card">
          <div class="stat-icon bg-primary">
            <i class="fas fa-calendar-check"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ activeSchedulesCount }}</div>
            <div class="stat-label">Active Schedules</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon bg-success">
            <i class="fas fa-list"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ schedules.length }}</div>
            <div class="stat-label">Total Schedules</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon bg-info">
            <i class="fas fa-clock"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ totalRestrictionsCount }}</div>
            <div class="stat-label">Restrictions</div>
          </div>
        </div>
      </div>-->

      <!-- Schedule List -->
      <div class="schedule-list-container" :style="{ 'width': '100%'}">
        <!-- Empty State -->
        <div v-if="schedules.length === 0" class="empty-state-modern">
          <div class="empty-icon">
            <i class="fas fa-calendar-times"></i>
          </div>
          <h5 class="empty-title">No Schedules Yet</h5>
          <p class="empty-text">Start creating schedules to manage your child's device usage effectively</p>
          <button class="btn btn-primary" @click="navigateToCreate()">
            <i class="fas fa-plus-circle mr-1"></i>Create Your First Schedule
          </button>
        </div>

        <!-- Schedule List Items -->
        <div v-else class="schedules-list text-left">
          <div 
            v-for="schedule in schedules" 
            :key="schedule._id" 
            class="schedule-item" 
            :class="{ 'schedule-inactive': !schedule.isActive }"
          >
            <!-- Schedule Header -->
            <div class="schedule-item-header">
              <div class="schedule-title-section">
                <div class="schedule-icon" :class="getScheduleIconClass(schedule.type)">
                  <i :class="getScheduleIcon(schedule.type)"></i>
                </div>
                <div class="schedule-title-info">
                  <h5 class="schedule-name">{{ schedule.label || 'Unnamed Schedule' }}</h5>
                  <div class="schedule-meta">
                    <span class="badge badge-modern" :class="getScheduleTypeBadge(schedule.type)">
                      {{ getScheduleTypeText(schedule.type) }}
                    </span>
                    <span class="schedule-status-text" :class="schedule.isActive ? 'text-success' : 'text-muted'">
                      <i class="fas fa-circle"></i> {{ schedule.isActive ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="schedule-actions">
                <div class="custom-switch-modern">
                  <input 
                    type="checkbox" 
                    :id="'switch-' + schedule._id" 
                    :checked="schedule.isActive"
                    @change="toggleScheduleStatus(schedule)"
                    :disabled="schedule.isToggling"
                  >
                  <label :for="'switch-' + schedule._id"></label>
                </div>
              </div>
            </div>

            <!-- Schedule Content -->
            <div class="schedule-item-content">
              <!-- Days Section -->
              <div class="schedule-section">
                <div class="section-label">
                  <i class="fas fa-calendar-week"></i>
                  <span>Active Days</span>
                </div>
                <div class="days-list">
                  <span 
                    v-for="day in schedule.days" 
                    :key="day" 
                    class="day-badge" 
                    :class="{ 'weekend-badge': isWeekend(day) }"
                  >
                    {{ getDayShort(day) }}
                  </span>
                </div>
              </div>

              <!-- Restrictions Section -->
              <div class="schedule-section">
                <div class="section-label">
                  <i class="fas fa-clock"></i>
                  <span>Restrictions ({{ schedule.restrictions.length }})</span>
                </div>
                <div class="time-slots-list">
                  <div 
                    v-for="(restriction, index) in schedule.restrictions" 
                    :key="index" 
                    class="time-slot-item" 
                    :class="getRestrictionClass(restriction.mode)"
                  >
                    <div class="time-slot-left">
                      <div class="time-slot-icon" :class="getRestrictionIconClass(restriction.mode)">
                        <i :class="getRestrictionIcon(restriction.mode)"></i>
                      </div>
                      <div class="time-slot-info">
                        <div class="time-range">
                          <i class="far fa-clock mr-1"></i>
                          <strong v-if="restriction.mode === 'sleep' || restriction.mode === 'apps'">
                            {{ formatTime(restriction.startTime) }} - {{ formatTime(restriction.endTime) }}
                          </strong>
                          <strong v-else-if="restriction.mode === 'limit'">
                            Daily Limit: {{ restriction.dailyLimitMinutes }} minutes
                          </strong>
                        </div>
                        <div v-if="restriction.mode === 'apps' && restriction.categories && restriction.categories.length > 0" class="time-slot-label">
                          Categories: {{ restriction.categories.join(', ') }}
                        </div>
                      </div>
                    </div>
                    <div class="time-slot-right">
                      <span class="mode-badge" :class="getModeBadgeClass(restriction.mode)">
                        <i :class="getRestrictionIcon(restriction.mode)"></i>
                        {{ getModeText(restriction.mode) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Schedule Footer -->
            <div class="schedule-item-footer">
              <button 
                class="btn btn-sm btn-outline-edit" 
                @click="editSchedule(schedule)"
                :disabled="schedule.isDeleting"
              >
                <i class="fas fa-edit mr-1"></i>Edit
              </button>
              <button 
                class="btn btn-sm btn-outline-delete" 
                @click="confirmDelete(schedule)"
                :disabled="schedule.isDeleting"
              >
                <span v-if="schedule.isDeleting">
                  <i class="fas fa-spinner fa-spin mr-1"></i>Deleting...
                </span>
                <span v-else>
                  <i class="fas fa-trash-alt mr-1"></i>Delete
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Delete Confirmation Modal -->
    <div 
      v-if="deleteModal.show" 
      class="modal-overlay" 
      @click.self="deleteModal.show = false"
    >
      <div class="modal-content">
        <div class="modal-header-custom">
          <h6>
            <i class="fas fa-exclamation-triangle text-danger mr-2"></i>
            Confirm Delete
          </h6>
        </div>
        <div class="modal-body-custom">
          <p>Are you sure you want to delete the schedule <strong>"{{ deleteModal.schedule?.label }}"</strong>?</p>
          <p class="text-muted small mb-0">This action cannot be undone.</p>
        </div>
        <div class="modal-footer-custom">
          <button 
            class="btn btn-secondary btn-sm" 
            @click="deleteModal.show = false"
            :disabled="deleteModal.isDeleting"
          >
            Cancel
          </button>
          <button 
            class="btn btn-danger btn-sm" 
            @click="deleteSchedule()"
            :disabled="deleteModal.isDeleting"
          >
            <span v-if="deleteModal.isDeleting">
              <i class="fas fa-spinner fa-spin mr-1"></i>Deleting...
            </span>
            <span v-else>
              <i class="fas fa-trash mr-1"></i>Delete
            </span>
          </button>
        </div>
      </div>
    </div>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import ScheduleService from "../../services/ScheduleService.js"

export default {
  name: 'Schedule',
  components: {
    KidHeader,
    KidFooter,
  },
  data() {
    return {
      profileId: this.$route.query.profileId,
      serialNumber: this.$route.query.serialNumber,
      schedules: [],
      isLoading: false,
      deleteModal: {
        show: false,
        schedule: null,
        isDeleting: false
      }
    }
  },
  computed: {
    activeSchedulesCount() {
      return this.schedules.filter(s => s.isActive).length;
    },
    totalRestrictionsCount() {
      return this.schedules.reduce((total, schedule) => {
        return total + (schedule.restrictions ? schedule.restrictions.length : 0);
      }, 0);
    }
  },
  methods: {
    async loadSchedules() {
      this.isLoading = true;
      
      try {
        const response = await ScheduleService.getSchedules(
          this.profileId,
          this.serialNumber
        );
        
        if (response.data.success) {
          this.schedules = (response.data.data || []).map(schedule => ({
            ...schedule,
            isToggling: false,
            isDeleting: false
          }));
        } else {
          this.$toast.error(response.data.message || 'Failed to load schedules');
        }
      } catch (error) {
        console.error('Error loading schedules:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to load schedules');
      } finally {
        this.isLoading = false;
      }
    },
    
    navigateToCreate() {
      this.$router.push({ 
        path: '/create-schedule', 
        query: { 
          profileId: this.profileId, 
          serialNumber: this.serialNumber 
        } 
      });
    },
    
    editSchedule(schedule) {
      this.$router.push({ 
        path: '/create-schedule', 
        query: { 
          profileId: this.profileId, 
          serialNumber: this.serialNumber,
          scheduleId: schedule._id,
          edit: 'true'
        } 
      });
    },
    
    confirmDelete(schedule) {
      this.deleteModal.schedule = schedule;
      this.deleteModal.show = true;
    },
    
    async deleteSchedule() {
      if (!this.deleteModal.schedule) return;
      
      this.deleteModal.isDeleting = true;
      const schedule = this.deleteModal.schedule;
      
      // Also set the schedule's deleting state
      schedule.isDeleting = true;
      
      try {
        const response = await ScheduleService.deleteSchedule(
          schedule._id,
          {
            profileId: this.profileId,
            serialNumber: this.serialNumber
          }
        );
        
        if (response.data.success) {
          this.$toast.success('Schedule deleted successfully');
          
          // Remove from local array
          const index = this.schedules.findIndex(s => s._id === schedule._id);
          if (index > -1) {
            this.schedules.splice(index, 1);
          }
          
          // Close modal
          this.deleteModal.show = false;
          this.deleteModal.schedule = null;
        } else {
          this.$toast.error(response.data.message || 'Failed to delete schedule');
        }
      } catch (error) {
        console.error('Error deleting schedule:', error);
        this.$toast.error(error.response?.data?.message || 'Failed to delete schedule');
      } finally {
        this.deleteModal.isDeleting = false;
        schedule.isDeleting = false;
      }
    },
    
    async toggleScheduleStatus(schedule) {
      // Prevent multiple simultaneous toggles
      if (schedule.isToggling) return;
      
      const newStatus = !schedule.isActive;
      const oldStatus = schedule.isActive;
      
      // Optimistically update UI
      schedule.isActive = newStatus;
      schedule.isToggling = true;
      
      try {
        const response = await ScheduleService.toggleScheduleStatus(
          schedule._id,
          {
            profileId: this.profileId,
            serialNumber: this.serialNumber,
            isActive: newStatus
          }
        );
        
        if (response.data.success) {
          this.$toast.success(`Schedule ${newStatus ? 'activated' : 'deactivated'} successfully`);
        } else {
          // Revert on failure
          schedule.isActive = oldStatus;
          this.$toast.error(response.data.message || 'Failed to update schedule status');
        }
      } catch (error) {
        console.error('Error toggling schedule:', error);
        
        // Revert on error
        schedule.isActive = oldStatus;
        
        // Handle conflict error (409)
        if (error.response && error.response.status === 409) {
          const data = error.response.data;
          
          // Build conflict message
          let message = 'Cannot activate schedule due to conflicts:\n\n';
          if (data.conflicts && data.conflicts.length > 0) {
            data.conflicts.forEach(c => {
              message += `• ${c.message}\n`;
            });
          }
          
          // Show alert with conflicts
          alert(message);
          this.$toast.error('Schedule has conflicts with other active schedules');
        } else {
          this.$toast.error(error.response?.data?.message || 'Failed to update schedule status');
        }
      } finally {
        schedule.isToggling = false;
      }
    },
    
    // Helper methods for UI display
    getScheduleTypeBadge(type) {
      const badges = {
        custom: 'badge-custom-type',
        weekday: 'badge-weekday-type',
        weekend: 'badge-weekend-type',
        everyday: 'badge-everyday-type'
      };
      return badges[type] || 'badge-custom-type';
    },
    
    getScheduleTypeText(type) {
      const texts = {
        custom: 'Custom',
        weekday: 'Weekdays',
        weekend: 'Weekend',
        everyday: 'Every Day'
      };
      return texts[type] || 'Custom';
    },
    
    getScheduleIcon(type) {
      const icons = {
        custom: 'fas fa-sliders-h',
        weekday: 'fas fa-briefcase',
        weekend: 'fas fa-calendar-week',
        everyday: 'fas fa-redo'
      };
      return icons[type] || 'fas fa-calendar';
    },
    
    getScheduleIconClass(type) {
      const classes = {
        custom: 'icon-custom',
        weekday: 'icon-weekday',
        weekend: 'icon-weekend',
        everyday: 'icon-everyday'
      };
      return classes[type] || 'icon-custom';
    },
    
    getDayShort(day) {
      const shorts = {
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun'
      };
      return shorts[day] || day;
    },
    
    isWeekend(day) {
      return day === 'saturday' || day === 'sunday';
    },
    
    getRestrictionClass(mode) {
      const classes = {
        sleep: 'sleep-slot',
        limit: 'limit-slot',
        apps: 'apps-slot'
      };
      return classes[mode] || '';
    },
    
    getRestrictionIcon(mode) {
      const icons = {
        sleep: 'fas fa-ban',
        limit: 'fas fa-hourglass-half',
        apps: 'fas fa-mobile-alt'
      };
      return icons[mode] || 'fas fa-clock';
    },
    
    getRestrictionIconClass(mode) {
      const classes = {
        sleep: 'icon-sleep',
        limit: 'icon-limit',
        apps: 'icon-apps'
      };
      return classes[mode] || 'icon-default';
    },
    
    getModeBadgeClass(mode) {
      const classes = {
        sleep: 'mode-sleep',
        limit: 'mode-limit',
        apps: 'mode-apps'
      };
      return classes[mode] || '';
    },
    
    getModeText(mode) {
      const texts = {
        sleep: 'Sleep Mode',
        limit: 'Daily Limit',
        apps: 'Apps Restriction'
      };
      return texts[mode] || mode;
    },
    
    formatTime(time) {
      if (!time) return '';
      // Convert 24h to 12h format
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
  },
  
  mounted() {
    this.loadSchedules();
  }
}
</script>

<style scoped>
.back-home {
  display: block;
  width: 100%;
  margin-bottom: 15px;
  font-size: 1rem;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.loading-container .spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Schedule Header */
.schedule-header {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%);
  padding: 15px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(99, 102, 241, 0.2);
  margin-bottom: 25px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-text h4 {
  color: white;
  font-weight: 700;
  font-size: 1.8rem;
  margin: 0;
}

.header-text p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
}

.btn-add-schedule {
  background: white;
  color: #6366f1;
  border: none;
  padding: 12px 10px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-add-schedule:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  background: #f8f9fa;
  color: #8b5cf6;
}

/* Stats Container */
.stats-container {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  overflow-x: auto;
}

.stat-card {
  background: white;
  padding: 15px 15px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 200px;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 35px;
  height: 35px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.stat-icon.bg-primary {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
}

.stat-icon.bg-success {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-icon.bg-info {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
}

/* Empty State */
.empty-state-modern {
  background: white;
  padding: 80px 40px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon i {
  font-size: 3rem;
  color: #9ca3af;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
}

.empty-text {
  color: #6b7280;
  font-size: 1.05rem;
  margin-bottom: 30px;
}

/* Schedule List Items */
.schedules-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.schedule-item {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  overflow: hidden;
}

.schedule-item:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  border-color: #6366f1;
  transform: translateY(-2px);
}

.schedule-item.schedule-inactive {
  opacity: 0.7;
}

.schedule-item.schedule-inactive:hover {
  opacity: 1;
}

/* Schedule Item Header */
.schedule-item-header {
  padding: 10px;
  background: linear-gradient(to right, #f9fafb, #ffffff);
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.schedule-title-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.schedule-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: white;
}

.schedule-icon.icon-custom {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.schedule-icon.icon-weekday {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.schedule-icon.icon-weekend {
  background: linear-gradient(135deg, #10b981, #059669);
}

.schedule-icon.icon-everyday {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.schedule-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 5px 0;
}

.schedule-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.badge-modern {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-custom-type {
  background: #dbeafe;
  color: #1e40af;
}

.badge-weekday-type {
  background: #ede9fe;
  color: #6b21a8;
}

.badge-weekend-type {
  background: #d1fae5;
  color: #065f46;
}

.badge-everyday-type {
  background: #fef3c7;
  color: #92400e;
}

.schedule-status-text {
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
}

.schedule-status-text i {
  font-size: 0.5rem;
}

/* Custom Switch Modern */
.custom-switch-modern {
  position: relative;
}

.custom-switch-modern input[type="checkbox"] {
  display: none;
}

.custom-switch-modern input[type="checkbox"]:disabled + label {
  opacity: 0.5;
  cursor: not-allowed;
}

.custom-switch-modern label {
  width: 60px;
  height: 32px;
  background: #d1d5db;
  border-radius: 50px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
}

.custom-switch-modern label:after {
  content: '';
  width: 26px;
  height: 26px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: 3px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.custom-switch-modern input:checked + label {
  background: #10b981;
}

.custom-switch-modern input:checked + label:after {
  left: 31px;
}

/* Schedule Content */
.schedule-item-content {
  padding: 25px;
}

.schedule-section {
  margin-bottom: 15px;
}

.schedule-section:last-child {
  margin-bottom: 0;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.section-label i {
  color: #6366f1;
}

/* Days List */
.days-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.day-badge {
  padding: 8px 16px;
  background: #f3f4f6;
  color: #1f2937;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 2px solid #e5e7eb;
  transition: all 0.2s ease;
}

.day-badge.weekend-badge {
  background: #fef3c7;
  color: #92400e;
  border-color: #fde68a;
}

/* Time Slots List */
.time-slots-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.time-slot-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f9fafb;
  border-radius: 10px;
  border-left: 4px solid #d1d5db;
  transition: all 0.2s ease;
}

.time-slot-item:hover {
  background: #f3f4f6;
  transform: translateX(4px);
}

.time-slot-item.sleep-slot {
  border-left-color: #ef4444;
  background: #fef2f2;
}

.time-slot-item.sleep-slot:hover {
  background: #fee2e2;
}

.time-slot-item.limit-slot {
  border-left-color: #f59e0b;
  background: #fffbeb;
}

.time-slot-item.limit-slot:hover {
  background: #fef3c7;
}

.time-slot-item.apps-slot {
  border-left-color: #8b5cf6;
  background: #f5f3ff;
}

.time-slot-item.apps-slot:hover {
  background: #ede9fe;
}

.time-slot-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time-slot-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.time-slot-icon.icon-sleep {
  background: #fecaca;
  color: #991b1b;
}

.time-slot-icon.icon-limit {
  background: #fde68a;
  color: #92400e;
}

.time-slot-icon.icon-apps {
  background: #ddd6fe;
  color: #6b21a8;
}

.time-range {
  font-size: 0.75rem;
  color: #1f2937;
}

.time-slot-label {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 2px;
}

.mode-badge {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.mode-badge.mode-sleep {
  background: #ef4444;
  color: white;
}

.mode-badge.mode-limit {
  background: #f59e0b;
  color: white;
}

.mode-badge.mode-apps {
  background: #8b5cf6;
  color: white;
}

/* Schedule Footer */
.schedule-item-footer {
  padding: 10px 10px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-outline-edit {
  border: 2px solid #6366f1;
  color: #6366f1;
  background: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-outline-edit:hover:not(:disabled) {
  background: #6366f1;
  color: white;
  transform: translateY(-2px);
}

.btn-outline-edit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-outline-delete {
  border: 2px solid #ef4444;
  color: #ef4444;
  background: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-outline-delete:hover:not(:disabled) {
  background: #ef4444;
  color: white;
  transform: translateY(-2px);
}

.btn-outline-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header-custom {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.modal-header-custom h6 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.modal-body-custom {
  padding: 20px;
}

.modal-body-custom p {
  margin-bottom: 12px;
  color: #333;
}

.modal-footer-custom {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #f9fafb;
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .btn-add-schedule {
    width: 100%;
  }
  
  .stats-container {
    flex-direction: column;
  }
  
  .stat-card {
    min-width: 100%;
  }
  
  .schedule-item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .schedule-actions {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
  
  .time-slot-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .time-slot-right {
    width: 100%;
  }
  
  .mode-badge {
    display: flex;
    width: 100%;
    justify-content: center;
  }
  
  .schedule-item-footer {
    justify-content: stretch;
  }
  
  .schedule-item-footer .btn {
    flex: 1;
  }
}
</style>